import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Clock, RefreshCw, Calendar, Globe, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { getApiUrl } from '../../utils/config';

interface CreditSchedule {
  utc_hour: number;
  utc_minute: number;
  utc_time: string;
  next_run_utc: string;
  current_time_utc: string;
  time_until_next_run: string;
  timezones: Array<{
    timezone: string;
    time: string;
    offset: string;
  }>;
}

interface CreditStatus {
  total_active_users: number;
  total_monthly_credits_needed: number;
  current_schedule: string;
  current_time_utc: string;
  last_grant?: {
    date: string;
    users_processed: number;
    credits_granted: number;
    status: string;
  };
}

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
];

export const CreditScheduleControl: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedTimezone, setSelectedTimezone] = useState('UTC');
  const [selectedHour, setSelectedHour] = useState(0);
  const [selectedMinute, setSelectedMinute] = useState(0);

  // Fetch current schedule
  const { data: scheduleData, isLoading: scheduleLoading } = useQuery<{ success: boolean; schedule: CreditSchedule }>({
    queryKey: ['creditSchedule'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/api/v1/tiers/admin/credit-schedule`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch schedule');
      return response.json();
    },
  });

  // Fetch credit status
  const { data: statusData, isLoading: statusLoading } = useQuery<{ success: boolean; status: CreditStatus }>({
    queryKey: ['creditStatus'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/api/v1/tiers/admin/monthly-credit-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch status');
      return response.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Update schedule mutation
  const updateScheduleMutation = useMutation({
    mutationFn: async ({ hour, minute, timezone }: { hour: number; minute: number; timezone: string }) => {
      const response = await fetch(
        `${getApiUrl()}/api/v1/tiers/admin/update-credit-schedule?hour=${hour}&minute=${minute}&timezone=${encodeURIComponent(timezone)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to update schedule');
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Credit schedule updated successfully!');
        queryClient.invalidateQueries({ queryKey: ['creditSchedule'] });
        queryClient.invalidateQueries({ queryKey: ['creditStatus'] });
      } else {
        toast.error(data.error || 'Failed to update schedule');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Manual trigger mutation
  const triggerCreditsMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getApiUrl()}/api/v1/tiers/admin/grant-all-monthly-credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to trigger credits');
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Monthly credits task queued successfully!');
        queryClient.invalidateQueries({ queryKey: ['creditStatus'] });
      } else {
        toast.error('Failed to trigger credits');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleUpdateSchedule = () => {
    updateScheduleMutation.mutate({
      hour: selectedHour,
      minute: selectedMinute,
      timezone: selectedTimezone,
    });
  };

  const handleTriggerNow = () => {
    if (confirm('Are you sure you want to manually trigger monthly credit granting now?')) {
      triggerCreditsMutation.mutate();
    }
  };

  // Get user's local timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="space-y-6">
      {/* Current Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Monthly Credit System Status
          </CardTitle>
          <CardDescription>
            Overview of the automated monthly credit granting system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : statusData?.success ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">Active Users</div>
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {statusData.status.total_active_users}
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium">Credits Needed</div>
                  <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {statusData.status.total_monthly_credits_needed}
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">Current Time (UTC)</div>
                  <div className="text-lg font-mono text-purple-900 dark:text-purple-100">
                    {new Date(statusData.status.current_time_utc).toLocaleTimeString()}
                  </div>
                </div>
              </div>

              {statusData.status.last_grant && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="font-medium">Last Grant</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Date</div>
                      <div className="font-medium">{new Date(statusData.status.last_grant.date).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Users Processed</div>
                      <div className="font-medium">{statusData.status.last_grant.users_processed}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Credits Granted</div>
                      <div className="font-medium">{statusData.status.last_grant.credits_granted}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Status</div>
                      <div className="font-medium capitalize">{statusData.status.last_grant.status}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>Failed to load status</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Schedule Configuration
          </CardTitle>
          <CardDescription>
            Configure when monthly credits should be granted automatically
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Current Schedule Display */}
            {scheduleData?.success && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="font-medium text-indigo-900 dark:text-indigo-100">Current Schedule</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">UTC Time:</span>
                    <span className="font-mono font-medium">{scheduleData.schedule.utc_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Next Run:</span>
                    <span className="font-medium">{new Date(scheduleData.schedule.next_run_utc).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Time Until Next Run:</span>
                    <span className="font-medium">{scheduleData.schedule.time_until_next_run}</span>
                  </div>
                </div>

                {/* Show times in different timezones */}
                <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-800">
                  <div className="text-xs font-medium text-indigo-900 dark:text-indigo-100 mb-2">
                    Schedule in Different Timezones:
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {scheduleData.schedule.timezones.map((tz) => (
                      <div key={tz.timezone} className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{tz.timezone}:</span>
                        <span className="font-mono">{tz.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Update Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                    <SelectTrigger id="timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {userTimezone && userTimezone !== selectedTimezone && (
                    <p className="text-xs text-gray-500">
                      Your timezone: {userTimezone}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hour">Hour (0-23)</Label>
                  <Input
                    id="hour"
                    type="number"
                    min="0"
                    max="23"
                    value={selectedHour}
                    onChange={(e) => setSelectedHour(parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="minute">Minute (0-59)</Label>
                  <Input
                    id="minute"
                    type="number"
                    min="0"
                    max="59"
                    value={selectedMinute}
                    onChange={(e) => setSelectedMinute(parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateSchedule}
                  disabled={updateScheduleMutation.isPending}
                  className="flex-1"
                >
                  {updateScheduleMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Clock className="mr-2 h-4 w-4" />
                      Update Schedule
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleTriggerNow}
                  disabled={triggerCreditsMutation.isPending}
                  variant="outline"
                  className="flex-1"
                >
                  {triggerCreditsMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Triggering...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Trigger Now (Manual)
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <div className="flex gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <p className="font-medium mb-1">Important Notes:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>The schedule runs daily at the specified time</li>
                      <li>Times are automatically converted to UTC for Celery</li>
                      <li>Changes take effect immediately</li>
                      <li>Manual trigger runs the task immediately, bypassing the schedule</li>
                      <li>For testing, set a time a few minutes from now</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
